/**
 * MentionLine — a dotted backlink line on the map
 *
 * Place at: src/components/map/MentionLine.jsx
 * Drawn by: CosmicMap.jsx, between the fork lines and the nodes.
 *
 * One line = "project A's description links to project B" ([[p:B]]).
 * Deliberately quieter than a fork line, and visibly a different KIND of line:
 *   - dotted, round caps (forks are solid)
 *   - curves to the OPPOSITE side, so a mention between two projects that
 *     are also fork-related never sits on top of the fork line
 *   - in focus, the dots march from source to target, so you can read
 *     which project did the mentioning
 *
 * States (decided by CosmicMap):
 *   'focus'   — one end is the selected project. Bright, marching.
 *   'ambient' — the "Show links" toggle is on. Faint; cross-zone a bit
 *               brighter, same hierarchy as fork lines.
 *   'dimmed'  — something else is selected. Nearly gone, never erased.
 *
 * Decoration only: pointer events pass straight through to whatever is
 * underneath, so these lines never steal a click from a node or a fork.
 */

import React from 'react';

const MentionLine = ({ id, sourceNode, targetNode, isCrossZone, state }) => {
  if (!sourceNode || !targetNode) return null;

  const dx = targetNode.x - sourceNode.x;
  const dy = targetNode.y - sourceNode.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;

  // Opposite sign to ConnectionLine's curvature.
  const curvature = -Math.min(dist * 0.18, 55);
  const mx = (sourceNode.x + targetNode.x) / 2 + (-dy / dist) * curvature;
  const my = (sourceNode.y + targetNode.y) / 2 + (dx / dist) * curvature;
  const pathD = `M ${sourceNode.x} ${sourceNode.y} Q ${mx} ${my} ${targetNode.x} ${targetNode.y}`;

  const sourceColor = sourceNode.zone?.color || '#71717a';
  const targetColor = targetNode.zone?.color || sourceColor;
  const gradId = `mg-${id}`;
  const useGradient = isCrossZone && state !== 'dimmed';

  let width;
  let opacity;
  if (state === 'focus') { width = isCrossZone ? 2.4 : 1.9; opacity = 0.9; }
  else if (state === 'ambient') { width = isCrossZone ? 1.6 : 1.2; opacity = isCrossZone ? 0.38 : 0.22; }
  else { width = 0.8; opacity = 0.05; }

  const stroke = useGradient ? `url(#${gradId})` : sourceColor;

  return (
    <g style={{ pointerEvents: 'none' }}>
      {useGradient && (
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse"
            x1={sourceNode.x} y1={sourceNode.y} x2={targetNode.x} y2={targetNode.y}>
            <stop offset="0%" stopColor={sourceColor} />
            <stop offset="100%" stopColor={targetColor} />
          </linearGradient>
        </defs>
      )}

      {state === 'focus' && (
        <path d={pathD} fill="none" stroke={stroke} strokeWidth={width + 5}
          strokeLinecap="round" opacity={0.08} />
      )}

      <path
        d={pathD}
        fill="none"
        stroke={stroke}
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray="0.5 7"
        opacity={opacity}
        style={{ transition: 'opacity 0.4s ease, stroke-width 0.3s ease' }}
      >
        {state === 'focus' && (
          // Dots march source -> target: "A mentions B" reads as a direction.
          <animate attributeName="stroke-dashoffset" from="15" to="0" dur="1.2s" repeatCount="indefinite" />
        )}
      </path>
    </g>
  );
};

export default React.memo(MentionLine);